import { matchRoutes, useLocation } from "react-router-dom";

import type { NavItem } from "../types";

export type SegmentName = "primarySegment" | "secondarySegment" | "tertiarySegment";

/**
 * Return type for the useNavSegments hook.
 */
export type NavSegments = {
  /** The first path segment after the app prefix. @example For "/hr/admin/permissions", returns "admin" */
  primarySegment: string;
  /** The second path segment. @example For "/hr/admin/permissions", returns "permissions" */
  secondarySegment: string;
  /** The nav item matching the primary segment. @example { key: "admin", label: "Admin", path: "/hr/admin", children: [...] } */
  primaryNavItem: NavItem | undefined;
  /** Whether the primary nav item has child routes. @example true if admin nav has children like permissions, org setup, etc. */
  primaryNavItemHasChildren: boolean;
  /** Array of child nav items, or empty array if none. @example [{ key: "permissions", ... }, { key: "orgSetup", ... }] */
  primaryNavItemChildren: NavItem[];
  /** The first child nav item, or undefined if no children. @example { key: "permissions", label: "Permissions", path: "/hr/admin/permissions" } */
  primaryNavFirstChild: NavItem | undefined;
  /** The child nav item matching the secondary segment. @example { key: "permissions", label: "Permissions", path: "/hr/admin/permissions" } */
  secondaryNavItem: NavItem | undefined;
  /** The grandchild nav item matching the tertiary segment. @example For "/hr/admin/org-setup/locations", { key: "locations", label: "Locations", path: "/hr/admin/org-setup/locations" } */
  tertiaryNavItem: NavItem | undefined;
};

function getPathSegments(pathname: string) {
  const [, , primarySegment, secondarySegment] = pathname.split("/");

  return { primarySegment, secondarySegment };
}

/**
 * Extracts navigation segments from the current URL path and finds corresponding nav items.
 *
 * @example
 * // For path "/hr/admin/permissions"
 * const { primarySegment, secondarySegment, primaryNavItem, secondaryNavItem } = useNavSegments(navItems);
 * // primarySegment = "admin"
 * // secondarySegment = "permissions"
 * // primaryNavItem = { key: "admin", label: "Admin", path: "/hr/admin", children: [...] }
 * // secondaryNavItem = { key: "permissions", label: "Permissions", path: "/hr/admin/permissions" }
 */
export function useNavSegments(navItems: NavItem[]): NavSegments {
  const location = useLocation();
  const { primarySegment = "", secondarySegment = "" } = getPathSegments(location.pathname);

  /** Finds the most specific child whose path pattern matches the start of the URL, including `:param` segments. */
  const findMatchingChild = (children: NavItem[] = []) =>
    matchRoutes(
      children.map((navItem) => ({ path: `${navItem.path}/*`, navItem })),
      location.pathname
    )?.[0].route.navItem;

  // TODO: test this
  const primaryNavItem = navItems.find(
    (n) => getPathSegments(n.path).primarySegment === primarySegment
  );
  const secondaryNavItem = findMatchingChild(primaryNavItem?.children);
  const tertiaryNavItem = findMatchingChild(secondaryNavItem?.children);

  return {
    primarySegment: primarySegment.slice(0, 1).toLocaleUpperCase() + primarySegment.slice(1),
    secondarySegment: secondarySegment.slice(0, 1).toLocaleUpperCase() + secondarySegment.slice(1),
    primaryNavItem,
    primaryNavItemHasChildren: (primaryNavItem?.children?.length ?? 0) > 0,
    primaryNavItemChildren: primaryNavItem?.children ?? [],
    primaryNavFirstChild: primaryNavItem?.children?.[0],
    secondaryNavItem,
    tertiaryNavItem,
  };
}
